/**
 * The six AI assistant personas (B3). Shared by the assistant panel's
 * persona picker (`AiChatPanel`) and the weekly-brief settings page
 * (`AiSettingsPage`) so the list of values and their labels can never drift
 * between the two surfaces — both send/store the same `persona` string the
 * backend keys its `persona.<value>` prompt on (A5's `apps/ai_chat/prompts.py`).
 */
export const AI_PERSONAS = [
  // E21: each carries a one-line description (from the backend's
  // `persona.<value>` voice prompt) so the picker explains itself.
  { value: 'pastor', label: 'Pastor', description: 'Warm and pastoral; numbers as part of the church’s story' },
  { value: 'coach', label: 'Coach', description: 'Direct and practical; wins, then the week ahead' },
  { value: 'analyst', label: 'Analyst', description: 'Precise and neutral; names uncertainty' },
  { value: 'mentor', label: 'Mentor', description: 'Patient; explains what each number means' },
  { value: 'encourager', label: 'Encourager', description: 'What’s going well first; concerns gently' },
  { value: 'strategist', label: 'Strategist', description: 'Terse; implications and what to do next' },
]

/** Just the value strings, for zod enums and membership checks. */
export const AI_PERSONA_VALUES = AI_PERSONAS.map((persona) => persona.value)

export const DEFAULT_PERSONA = 'coach'

/** Look up a persona's display label; falls back to the raw value for an unrecognized one. */
export function personaLabel(value) {
  return AI_PERSONAS.find((persona) => persona.value === value)?.label || value
}

const PERSONA_STORAGE_KEY = 'graphit.ai.persona'

/**
 * The viewer's last-chosen persona, read from localStorage. Falls back to
 * `DEFAULT_PERSONA` when nothing is stored, the stored value isn't one of
 * the six personas, or storage isn't available at all (private browsing,
 * disabled storage) — guarded so a browser without localStorage never
 * crashes the panel on mount.
 *
 * @returns {string}
 */
export function readPersistedPersona() {
  try {
    const stored = window.localStorage.getItem(PERSONA_STORAGE_KEY)
    return AI_PERSONA_VALUES.includes(stored) ? stored : DEFAULT_PERSONA
  } catch {
    return DEFAULT_PERSONA
  }
}

/**
 * Persist the viewer's chosen persona. Best-effort: a browser with storage
 * disabled just keeps the in-memory selection for the current session.
 *
 * @param {string} persona
 */
export function persistPersona(persona) {
  try {
    window.localStorage.setItem(PERSONA_STORAGE_KEY, persona)
  } catch {
    // Private mode or storage disabled — remembering is best-effort.
  }
}
